import React, { useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger, Input, Button, Separator } from '@duck/ui'
import { Filter, X, Trash2, ArrowUpNarrowWide, ArrowDownWideNarrow, Save, Check, Columns } from 'lucide-react'
import { useTar } from '@/modules/i18n'
import type { TableSortState, SortDirection } from './useTableSettings'
import { useFilterSets } from './useFilterSets'

interface FilterSetManagerProps {
  tableId: string
  sortColumn: string | null
  sortDirection: SortDirection
  filters: Record<string, string | string[]>
  visibleColumns: Record<string, boolean>
  defaultVisibleColumns: Record<string, boolean>
  filterLabels: Record<string, Record<string, string>>
  setSort: (columnKey: string | null, direction: SortDirection) => void
  clearColumnFilter: (columnKey: string) => void
  clearAllSettings: () => void
  applyFilterSet: (sort: TableSortState, filters: Record<string, string | string[]>, visibleColumns?: Record<string, boolean>, filterLabels?: Record<string, Record<string, string>>) => void
  columns: Array<{ key: string; label: string }>
}

export const FilterSetManager: React.FC<FilterSetManagerProps> = ({
  tableId,
  sortColumn,
  sortDirection,
  filters,
  visibleColumns,
  defaultVisibleColumns,
  filterLabels,
  setSort,
  clearColumnFilter,
  clearAllSettings,
  applyFilterSet,
  columns,
}) => {
  const { t } = useTar()
  const { filterSets, saveFilterSet, deleteFilterSet } = useFilterSets(tableId)
  const [saveName, setSaveName] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [justSaved, setJustSaved] = useState(false)

  const columnLabelMap = Object.fromEntries(columns.map(c => [c.key, c.label]))

  const activeFilterKeys = Object.keys(filters).filter(k => {
    const v = filters[k]
    return Array.isArray(v) ? v.length > 0 : typeof v === 'string' && v.trim().length > 0
  })

  const hasSort = sortColumn !== null && sortDirection !== null

  // Count hidden columns (columns that differ from defaults)
  const hiddenColumnCount = columns.filter(c => {
    const isVisible = visibleColumns[c.key] !== false
    const wasVisible = defaultVisibleColumns[c.key] !== false
    return isVisible !== wasVisible
  }).length

  const activeCount = activeFilterKeys.length + (hasSort ? 1 : 0) + (hiddenColumnCount > 0 ? 1 : 0)

  const getFilterValueLabel = (filterKey: string, value: string): string => {
    return filterLabels[filterKey]?.[value] ?? value
  }

  const formatFilterValue = (filterKey: string, value: string | string[]): string => {
    if (Array.isArray(value)) {
      const labels = value.map(v => getFilterValueLabel(filterKey, v))
      return labels.length <= 2 ? labels.join(', ') : `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`
    }
    return getFilterValueLabel(filterKey, value)
  }

  const handleSave = () => {
    const name = saveName.trim()
    if (!name) return
    saveFilterSet(
      name,
      { columnKey: sortColumn, direction: sortDirection },
      filters,
      visibleColumns,
      filterLabels,
    )
    setSaveName('')
    setJustSaved(true)
    setTimeout(() => setJustSaved(false), 1500)
  }

  const handleLoad = (set: typeof filterSets[number]) => {
    applyFilterSet(set.settings.sort, set.settings.filters, set.settings.visibleColumns, set.settings.filterLabels)
    setIsOpen(false)
  }

  const countSavedSetItems = (set: typeof filterSets[number]): string => {
    const parts: string[] = []
    const filterCount = Object.keys(set.settings.filters).length
    if (filterCount > 0) parts.push(`${filterCount} ${t('common.filters').toLowerCase()}`)
    if (set.settings.sort.columnKey) parts.push(t('common.sort').toLowerCase())
    if (set.settings.visibleColumns) {
      const changed = columns.filter(c => {
        const saved = set.settings.visibleColumns![c.key] !== false
        const def = defaultVisibleColumns[c.key] !== false
        return saved !== def
      }).length
      if (changed > 0) parts.push(`${changed} col`)
    }
    return parts.join(' + ')
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className={`flex items-center justify-center h-5 w-5 transition-colors ${activeCount > 0 ? 'text-accent' : 'text-muted-foreground hover:text-foreground'}`}>
          <Filter className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="max-h-[70vh] overflow-y-auto">
          {/* Active filters section */}
          <div className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">{t('common.activeFilters')}</span>
              {activeCount > 0 && (
                <button
                  onClick={clearAllSettings}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('common.clearAll')}
                </button>
              )}
            </div>

            {activeCount === 0 ? (
              <p className="text-xs text-muted-foreground py-1">{t('common.noActiveFilters')}</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {hasSort && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary/70 border border-border px-2 py-0.5 text-[11px]">
                    {sortDirection === 'asc' ? (
                      <ArrowUpNarrowWide className="h-3 w-3 text-accent" />
                    ) : (
                      <ArrowDownWideNarrow className="h-3 w-3 text-accent" />
                    )}
                    <span className="truncate max-w-[140px]">{columnLabelMap[sortColumn!] ?? sortColumn}</span>
                    <button
                      onClick={() => setSort(null, null)}
                      className="text-muted-foreground hover:text-foreground transition-colors ms-0.5"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                )}

                {activeFilterKeys.map(key => (
                  <span key={key} className="inline-flex items-center gap-1 rounded-full bg-secondary/70 border border-border px-2 py-0.5 text-[11px]">
                    <span className="font-medium">{columnLabelMap[key] ?? key}:</span>
                    <span className="truncate max-w-[120px]">{formatFilterValue(key, filters[key])}</span>
                    <button
                      onClick={() => clearColumnFilter(key)}
                      className="text-muted-foreground hover:text-foreground transition-colors ms-0.5"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}

                {hiddenColumnCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary/70 border border-border px-2 py-0.5 text-[11px]">
                    <Columns className="h-3 w-3 text-accent" />
                    <span>{hiddenColumnCount} col</span>
                  </span>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Save section */}
          <div className="p-3 space-y-2">
            <span className="text-sm font-medium text-foreground">{t('common.saveFilters')}</span>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder={t('common.filterSetName')}
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                className="h-8 text-xs"
                disabled={activeCount === 0}
              />
              <Button
                variant="outline"
                size="sm"
                className="h-8 shrink-0"
                onClick={handleSave}
                disabled={!saveName.trim() || activeCount === 0}
              >
                {justSaved ? <Check className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>

          {filterSets.length > 0 && (
            <>
              <Separator />

              {/* Saved filter sets */}
              <div className="p-3 space-y-2">
                <span className="text-sm font-medium text-foreground">{t('common.savedFilterSets')}</span>
                <div className="space-y-1">
                  {filterSets.map(set => (
                    <div
                      key={set.id}
                      className="flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 hover:bg-secondary/50 cursor-pointer group transition-colors"
                      onClick={() => handleLoad(set)}
                    >
                      <div className="min-w-0">
                        <span className="text-xs font-medium truncate block">{set.name}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {countSavedSetItems(set)}
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteFilterSet(set.id)
                        }}
                        className="shrink-0 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
